import React, { createRef, RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AmpInput from "../../common/AmpInput/AmpInput";
import { RootState } from "../../../redux/reducers/root";

import "./EditAccount.css";
import AmpCheckBox from "../../common/AmpCheckBox/AmpCheckBox";
import AmpDropdown from "../../common/AmpDropdown/AmpDropdown";
import { getSecurityQuestions } from "../../../redux/actions/auth/securityQuestion";
import { changeModalState } from "../../../redux/actions/modal/modal";
import AmpButton from "../../common/AmpButton/AmpButton";
import { resetUpdateState, updateUserInfo } from "../../../redux/actions/auth/manageUser";
import { setToastNotification } from "../../../redux/actions/toastNotification/toastNotification";
import AmpInlineLoadingSpinner from "../../common/AmpInlineLoadingSpinner/AmpInlineLoadingSpinner";
import i18n from "../../../i18n";
import PasswordValidator from "../../login/PasswordValidator/PasswordValidator";

export default function EditAccount(){

    const { t } = useTranslation(["profile", "login"]);

    const dispatch = useDispatch();

    const emailRef = useRef(null);
    const passwordRef = useRef(null);
    const securityQuestionRef = useRef(null);
    const languageRef = useRef(null);

    const [emailErrorMessage, setEmailErrorMessage] = useState<string | undefined>(undefined);
    const [confirmEmailErrorMessage, setConfirmEmailErrorMessage] = useState<string | undefined>(undefined);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<string | undefined>(undefined);
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState<string | undefined>(undefined);
    const [selectQuestionErrorMessage, setSelectQuestionErrorMessage] = useState<string | undefined>(undefined);
    const [customQuestionErrorMessage, setCustomQuestionErrorMessage] = useState<string | undefined>(undefined);
    const [securityAnswerErrorMessage, setSecurityAnswerErrorMessage] = useState<string | undefined>(undefined);

    const [disableConfirmEmail, setDisableConfirmEmail] = useState(false);
    const [disableConfirmPassword, setDisableConfirmPassword] = useState(true);
    const [showPassword, setShowPassword] = useState<"text" | "password">("password");
    const [hideClass, setHideClass] = useState<"" | " hide">(" hide");

    const manageUserInfoState: Record<string, unknown> = useSelector((state: RootState) => state.authReducer.getUser);

    const securityQuestionState: Record<string, unknown> = useSelector((state: RootState) => state.authReducer.getSecurityQuestion);

    const securityQuestionsState: Record<string, unknown> = useSelector((state: RootState) => state.authReducer.getSecurityQuestions);

    const userUpdateState: Record<string, unknown> = useSelector((state: RootState) => state.authReducer.updateUser);

    const [email, setEmail] = useState(manageUserInfoState.user_id ? manageUserInfoState.user_id as string : "");
    const [confirmEmail, setConfirmEmail] = useState(manageUserInfoState.user_id ? manageUserInfoState.user_id as string : "");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [securityQuestion, setSecurityQuestion] = useState(manageUserInfoState.challenge_question ? manageUserInfoState.challenge_question as string : "");
    // eslint-disable-next-line
    const [originalQuestionId, setOriginalQuestionId] = useState(manageUserInfoState.challenge_question ? manageUserInfoState.challenge_question as string : "");
    const [customQuestion, setCustomQuestion] = useState("");
    const [originalCustomQuestion, setOriginalCustomQuestion] = useState("");
    const [securityAnswer, setSecurityAnswer] = useState(manageUserInfoState.challenge_response ? manageUserInfoState.challenge_response as string : "");
    const [preferredLanguage, setPreferredLanguage] = useState(manageUserInfoState.pref_lang ? manageUserInfoState.pref_lang as string : "");

    const [securityQuestionOptions, setSecurityQuestionOptions] = useState<{ value: string, text: string }[]>([]);

    const languageOptions = [
        {value: "en_EN", text: t("4-english", {ns: "login"})},
        {value: "fr_FR", text: t("4-french", {ns: "login"})}
    ]

    useEffect(() => {  
        const userProfileId = manageUserInfoState.user_profile_id ?? window.localStorage.getItem("userProfileId");
        if (userProfileId) {
            dispatch(getSecurityQuestions(userProfileId as string));
        } else {
            dispatch(setToastNotification("Not allowed to access this resource", "error"));
        }
    // eslint-disable-next-line
    }, []);

    useEffect(() => {
		if (securityQuestionsState.status && securityQuestionsState.status !== 200) {
			dispatch(setToastNotification(securityQuestionsState.message as string ?? "Not allowed to access this resource", "error"));
		} else if (securityQuestionsState.questions) {
            const optionArray: {value: string, text: string}[] = [
                { value: "" , text: t("edit-account.select-question") as string},
                { value: "1", text: t('3-what-childhood-nick', {ns: "login"})},
                { value: "2", text: t('3-what-childhood-friend', {ns: "login"})},
                { value: "3", text: t('3-city-job-q', {ns: "login"})},
                { value: "4", text: t('3-what-spouse', {ns: "login"})},
            ];
            let questionIsCustom = true;

            (securityQuestionsState.questions as Record<string, string>[]).forEach((question) => {
                if (question.question_id as string === securityQuestion) {
                    questionIsCustom = false;
                }
                // optionArray.push({value: question.question_id, text: question.question_text});
            });
            optionArray.push({value: "create" , text: t("edit-account.create-question") as string});
            
            setSecurityQuestionOptions(optionArray);
            if (questionIsCustom) {
                setCustomQuestion(securityQuestionState.question_text as string);
                setOriginalCustomQuestion(securityQuestionState.question_text as string);
                setHideClass("");
                setSecurityQuestion("create");
            }
		}
	}, [securityQuestionsState, dispatch, securityQuestion, securityQuestionState.question_text, t]);

    useEffect(() => {
        if (userUpdateState.status) {
            let errorMessage = "";
            if (["changed", "static"].indexOf(userUpdateState.email as string) === -1) {
                errorMessage += userUpdateState.email as string;
            }
            if (["changed", "static"].indexOf(userUpdateState.password as string) === -1) {
                errorMessage += ((errorMessage !== "") ? "\n" : "") + userUpdateState.password as string;
            }
            if (["changed", "static"].indexOf(userUpdateState.question as string) === -1) {
                errorMessage += ((errorMessage !== "") ? "\n" : "") + userUpdateState.question as string;
            }
            if (["changed", "static"].indexOf(userUpdateState.language as string) === -1) {
                errorMessage += ((errorMessage !== "") ? "\n" : "") + userUpdateState.language as string;
            }
            if (errorMessage !== ""){
                dispatch(setToastNotification(errorMessage, "error"));
            }
            dispatch(resetUpdateState());
        }
    }, [userUpdateState, dispatch]);

    const validateEmail = (val:string) => {
        if (!val.match(/^[a-zA-Z0-9_.%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)){
            return false;
        }    
        return true;
    }

    const validateEmailField = (val: string) => {
        const inputEmail = val.trim();
        setEmail(val.trim());
        const emailIsValid = validateEmail(inputEmail);
        setEmailErrorMessage(emailIsValid ? undefined : t("edit-account.email-error-message") as string);
        setDisableConfirmEmail(!emailIsValid);
    }

    const validateConfirmEmailField = (val: string) => {
        const inputConfirmEmail = val.trim();
        setConfirmEmail(inputConfirmEmail);
        if (!validateEmail(inputConfirmEmail)) {
            setConfirmEmailErrorMessage(t("edit-account.email-error-message") as string);
        } else if (inputConfirmEmail !== email && validateEmail(email)) {
            setConfirmEmailErrorMessage(t("edit-account.confirm-email-error-message") as string);
        } else {
            setConfirmEmailErrorMessage(undefined);
        }
    }

    const validatePassword = (val: string) => {
        const regex = /^(?=.*\d)(?=.*[A-Z])(?=.*[a-z])(?=.*[^a-zA-Z0-9])([^\s]){11,}$/
        return regex.test(val);
    }

    const validatePasswordField = (val: string) => {
        setPassword(val);
        const passwordIsValid = validatePassword(val);
        setPasswordErrorMessage(passwordIsValid || val === "" ? undefined : t("edit-account.password-error-message") as string);
        setDisableConfirmPassword(!passwordIsValid);
    }

    const validateConfirmPasswordField = (val: string) => {
        setConfirmPassword(val);
        setConfirmPasswordErrorMessage(val === password ? undefined : t("edit-account.confirm-password-error-message") as string);
    }

    const displayPassword = (e: React.ChangeEvent) => {
        setShowPassword((e.currentTarget as HTMLInputElement).checked ? "text" : "password");
    }

    const showHideCreateQuestionInput = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const selectedValue = event.target.value;
        setSecurityQuestion(selectedValue);
        if (selectedValue === "") {
            setSelectQuestionErrorMessage(t("edit-account.custom-question-error-message") as string);
            setHideClass(" hide");
        } else if (selectedValue === "create") {
            setHideClass("");
            setSelectQuestionErrorMessage(undefined);
        }  
        else {
            setHideClass(" hide");
            setSelectQuestionErrorMessage(undefined);
        }
	}

    const validateCustomQuestion = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCustomQuestion(e.currentTarget.value);
    }

    const validateSecurityAnswer = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSecurityAnswer(e.currentTarget.value);
        (e.currentTarget.value.length >= 4 ) ? setSecurityAnswerErrorMessage(undefined) : setSecurityAnswerErrorMessage(t("edit-account.security-answer-error-message") as string);
    }

    const changeSelectedLanguage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = event.target.value;
        setPreferredLanguage(selectedValue);
    }

    const submitForm = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        let dataError = false;
        let emailChanged = "false";
        let passwordChanged = "false";
        let questionChanged = "false";
        let answerChanged = "false"
        let questionisCustom = false;
        let languageChanged = "false";

        const userId = manageUserInfoState.user_profile_id;
        if (userId) {
            const userEmail = manageUserInfoState.user_id;
            if (userEmail) {
                if (userEmail !== email) {
                    if (validateEmail(email)){
                        if (email === confirmEmail){
                            emailChanged ="true"
                        } else {
                            emailChanged = "error";
                            setConfirmEmailErrorMessage(t("edit-account.confirm-email-error-message") as string);
                        }
                    }
                    else {
                        emailChanged = "error";
                        setEmailErrorMessage(t("edit-account.email-error-message") as string);
                    } 
                }
            } else {
                dataError = true;
            }

            const userPassword = manageUserInfoState.password;
            if (password !== ""){
                if (userPassword) {   
                    if (validatePassword(password)) {
                        if (password === confirmPassword) {
                            passwordChanged = "true";
                        } else {
                            passwordChanged = "error";
                            setConfirmPasswordErrorMessage(t("edit-account.confirm-password-error-message") as string);
                        }             
                    }
                    else {
                        passwordChanged = "error";
                        setPasswordErrorMessage(t("edit-account.password-error-message") as string);
                    }                
                } else {
                    dataError = true;
                }
            }  

            const userQuestion = manageUserInfoState.challenge_question;
            const userAnswer = manageUserInfoState.challenge_response;
            if (userQuestion && userAnswer) {
                if (securityQuestion === "") {
                    questionChanged = "error";
                    setSelectQuestionErrorMessage(t("edit-account.custom-question-error-message") as string);
                } 
                else if (securityQuestion === "create") {
                    if (customQuestion === "") {
                        questionChanged = "error";
                        setCustomQuestionErrorMessage(t("edit-account.custom-question-error-message") as string);
                    } else if (customQuestion !== originalCustomQuestion) {
                        questionChanged = "true";
                        questionisCustom = true;
                    } 
                } else {
                    if (securityQuestion !== userQuestion) {
                        questionChanged = "true";
                    }
                }

                if (securityAnswer.length < 4) {
                    answerChanged = "error";
                    setSecurityAnswerErrorMessage(t("edit-account.security-answer-error-message") as string);
                } else if (securityAnswer !== userAnswer) {
                    answerChanged = "true";
                }
            } else {
                dataError = true;
            }

            const userLanguage = manageUserInfoState.pref_lang;
            if (userLanguage) {
                if (userLanguage !== preferredLanguage) {
                    languageChanged = "true";
                }
            } else {
                dataError = true;
            }

            if (dataError) {
                dispatch(setToastNotification(t("error") as string, "error"));
            } else {
                const checkArray = [emailChanged, passwordChanged, questionChanged, answerChanged, languageChanged];
                if (checkArray.includes("error")) {
                    let scrollTo: RefObject<HTMLLabelElement> = createRef<HTMLLabelElement>();
                    if (languageChanged === "error") {
                        scrollTo = languageRef;
                    }
                    if (questionChanged === "error" || answerChanged === "error") {
                        scrollTo = securityQuestionRef;
                    }
                    if (passwordChanged === "error") {
                        scrollTo = passwordRef;
                    }
                    if (emailChanged === "error") {
                        scrollTo = emailRef;
                    }
                    scrollTo.current && window.scrollTo(0, scrollTo.current.offsetTop); 
                } else if (checkArray.includes("true")){
                    const payload: Record<string, unknown> = {
                        "form_data": {
                            "email": {
                                "changed": emailChanged === "true",
                                "old_email": userEmail as string,
                                "new_email": email
                            },
                            "password": {
                                "changed": passwordChanged === "true",
                                "old_password": manageUserInfoState.password ? manageUserInfoState.password as string : "",
                                "new_password": password
                            },
                            "question": {
                                "changed": questionChanged === "true" || answerChanged === "true",
                                "is_custom": questionisCustom,
                                "question": questionisCustom ? customQuestion : (questionChanged === "true" ? securityQuestion : originalQuestionId),
                                "answer": securityAnswer
                            },
                            "language": {
                                "changed": languageChanged === "true",
                                "preferred_language": preferredLanguage
                            }
                        }
                    }
                    dispatch(updateUserInfo(userId as string, payload));
                    if (languageChanged) {
                        window.localStorage.setItem("prefLang", preferredLanguage === "fr_FR" ? "fr": "en");
                        if (preferredLanguage === "fr_FR") {
                            i18n.changeLanguage('fr');  
                        } else {
                            i18n.changeLanguage('en');
                        }
                    }
                } else {
                    dispatch(changeModalState("modal-dismiss"));
                }
            }            
        }
        else {
            dispatch(setToastNotification(t("error") as string, "error"));
        }        
    }

    return (
        <>
            <div className="edit-account-body">
                <form className="edit-account-form-container">
                <span className="edit-account-required-text">*{t("edit-account.required")}</span>
                    <div className="edit-account-form">
                        <div className="label-div"><label htmlFor="email" ref={emailRef}>{t("edit-account.email")} <span className="edit-account-required-star">*</span></label></div>
                        <div><AmpInput name="email" id="email" type="text" value={manageUserInfoState.email ? (manageUserInfoState.email as string) : ""} onInput={validateEmailField} noTimer={true} errorMessage={emailErrorMessage} testid="email-test"/></div>
                        <div><ul><li>{t("edit-account.email-guide") as string}</li></ul></div>
                        <div className="label-div"><label htmlFor="confirmEmail">{t("edit-account.confirm-email")} <span className="edit-account-required-star">*</span></label></div>
                        <div><AmpInput name="confirmEmail" id="confirmEmail" type="text" value={manageUserInfoState.email ? (manageUserInfoState.email as string) : ""} onInput={validateConfirmEmailField} noTimer={true} disabled={disableConfirmEmail} errorMessage={confirmEmailErrorMessage} testid="confirm-email-test"/></div>
                        <div>
                            <ul><li>{t("edit-account.email-guide") as string}</li>
                            <li>{t("edit-account.confirm-email-guide") as string}</li></ul>
                        </div>
                        <div className="section-space" />
                        <span className="password-section">
                            <div className="label-div"><label htmlFor="password" ref={passwordRef}>{t("edit-account.password")}</label></div>
                            <div><AmpInput name="password" id="password" type={showPassword} onInput={validatePasswordField} noTimer={true} autoComplete="new-password"  errorMessage={passwordErrorMessage} testid="password-test"/></div>
                            <div className="label-div"><label htmlFor="confirmPassword">{t("edit-account.confirm-password")}</label></div>
                            <div>
                                <AmpInput name="confirmPassword" id="confirmPassword" type={showPassword} onInput={validateConfirmPasswordField} noTimer={true} autoComplete="new-password" disabled={disableConfirmPassword} errorMessage={confirmPasswordErrorMessage} testid="confirm-password-test"/>
                                <div className="show-password-checkbox">
                                    <AmpCheckBox id="showPassword" label={t("edit-account.show-password") as string} hasToolTip={false} toolTip="" handleCheck={displayPassword} />
                                </div>
                            </div>
                        </span>
                        <PasswordValidator password={password} />
                        <div className="label-div"><label htmlFor="securityQuestion" ref={securityQuestionRef}>{t("edit-account.security-question")} <span className="edit-account-required-star">*</span></label></div>
                        <div>{ securityQuestionOptions.length === 0 ?
                                <AmpInlineLoadingSpinner /> :
                                <AmpDropdown options={securityQuestionOptions} value={securityQuestion} name="securityQuestion" id="securityQuestion" className="profile-dropdown" onChange={showHideCreateQuestionInput} errorMessage={selectQuestionErrorMessage} testid="question-dropdown-test"/>
                            }
                        </div>
                        <div />
                        <div className={`label-div${hideClass}`}><label className="create-question" htmlFor="customQuestion">{t("edit-account.custom-question")}</label></div>
                        <div className={`create-question${hideClass}`}><AmpInput name="customQuestion" id="customQuestion" type="text" value={customQuestion} onChange={validateCustomQuestion} errorMessage={customQuestionErrorMessage} /></div>
                        <div className={`create-question${hideClass}`}><ul><li>{t("edit-account.custom-question-guide") as string}</li></ul></div>
                        <div className="label-div"><label htmlFor="securityAnswer">{t("edit-account.security-answer")} <span className="edit-account-required-star">*</span></label></div>
                        <div><AmpInput name="securityAnswer" id="securityAnswer" type="text" value={securityAnswer} onChange={validateSecurityAnswer} errorMessage={securityAnswerErrorMessage}  testid="custom-answer-test"/></div>
                        <div><ul><li>{t("edit-account.security-answer-guide") as string}</li></ul></div>
                    </div>
                    <div className="edit-account-form blue-background">
                        <div className="label-div"><label htmlFor="preferredLanguage" ref={languageRef}>{t("edit-account.language")} <span className="edit-account-required-star">*</span></label></div>
                        <div><AmpDropdown options={languageOptions} value={preferredLanguage} name="preferredLanguage" id="preferredLanguage" className="profile-dropdown" onChange={changeSelectedLanguage}  testid="language-dropdown-test"/></div>
                        <div />
                        <div className="assist-information">
                            <p>{t("edit-account.assist-paragraph-1") as string}</p>
                            <p>{t("edit-account.assist-paragraph-2-1") as string} <span className="no-wrap">{t("edit-account.assist-paragraph-2-2") as string}</span> {t("edit-account.assist-paragraph-2-3") as string}</p>
                            <h2 className="assist-question">{t("edit-account.assist-question") as string}</h2>
                            <p><a href="#/amp/contact-us" onClick={() => {dispatch(changeModalState("modal-dismiss"))}}>{t("edit-account.assist-hyperlink-text") as string}</a> {t("edit-account.assist-paragraph-3") as string}</p>
                        </div>
                    </div>
                    <div className="update-account-button-container"><AmpButton onClick={submitForm} type="submit" text={t("edit-account.update-account-button") as string} disabled={userUpdateState.loading as boolean} /></div>
                </form>
            </div>
        </>
    )
}